import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminTransferRequestState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}
const defaultColumnList = [
  {
    name: 'booking_id',
    label_key: 'booking_id',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 1,
  },
  {
    name: 'order_number',
    label_key: 'order_#',
    allow_list: false,
    allow_filter: true,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'text',
    filter_placeholder: 'order_no_all',
    order: 9,
  },
  {
    name: 'ffl',
    label_key: 'ffl',
    allow_list: false,
    allow_filter: true,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'ffls_all',
    // Covers both registered FFLs and the non-member ones invited by license only - a plain
    // `ffl` asyncKey (fflOptions) only ever searches the FFL collection, which is why an invited
    // FFL's name used to return no results here.
    asyncKey: 'transfer_ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 10,
  },
  {
    name: 'ffl_direction',
    label_key: 'sending_receiving',
    allow_list: false,
    allow_filter: true,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'sending_receiving',
    selectKey: 'transfer_direction',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 11,
  },
  {
    name: 'customer_buyer',
    label_key: 'customer',
    allow_list: false,
    allow_filter: true,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'asyncselect',
    asyncKey: 'customer',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 12,
  },
  {
    name: 'product',
    label_key: 'product',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'ffl_from',
    label_key: 'ffl_from',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: false,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'ffl_to',
    label_key: 'ffl_to',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 4,
  },
  {
    name: 'customer',
    label_key: 'customer',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'status',
    label_key: 'status',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'status_all',
    selectKey: 'transfer_status',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 6,
  },
  {
    name: 'created_at',
    label_key: 'created_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 7,
  },
];
// The filter-only entries above (transfer_id, order_number, ffl, ffl_direction, customer_buyer)
// have no matching field in the exported row, so they're left out of the export column picker.
const exportColumnList = defaultColumnList
  .filter((list) => list.allow_list)
  .map((list) => ({
    ...list,
    allow_list: true,
    show_list_checkbox: true,
  }));
const initialState: AdminTransferRequestState = {
  excludedFilter: [],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminTransferRequestSlice = createSlice({
  name: 'adminTransferRequest',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setTransferRequestTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setTransferRequestTableColumnList,
} = adminTransferRequestSlice.actions;

export default adminTransferRequestSlice.reducer;
